/**
 * InvoiceIntakeDashboard — "Upload Invoices" power dashboard.
 *
 * Menu (big iconised buttons):
 *   1. Upload invoices to a specific project and task — pick ONE target,
 *      then drop one or many files; every file goes there.
 *   2. Upload one or more invoices to different projects and tasks — drop
 *      files first, then choose a target per file (recently used targets
 *      are offered first so several can go to the same place in one click).
 *
 * Target search: one box searching company names, project titles and task
 * titles; it runs 450 ms after typing pauses, never on every keystroke.
 *
 * Each dropped file uploads immediately; the server extracts text (PDF,
 * DOCX, CSV, TXT — no AI) and parses supplier, VAT number, invoice number,
 * dates, net/VAT/gross and line items. Images (scans) are kept for manual
 * entry. Clicking a file opens a full-screen review: the document on the
 * left, the interpreted fields on the right — edit anything, then APPROVE
 * to create the received invoice with its line items and attach the
 * document to the project. Nothing reaches the invoice ledger until then.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Input, Select, Badge, HStack, VStack, IconButton, Spinner, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter,
  FormControl, FormLabel, Table, Thead, Tbody, Tr, Th, Td, Tooltip, Divider, Image, Switch, Tag,
} from '@chakra-ui/react';
import { FiUpload, FiFolder, FiLayers, FiSearch, FiArrowLeft, FiFileText, FiCheckCircle, FiAlertCircle,
  FiTrash2, FiRefreshCw, FiEdit3, FiPlus, FiX, FiImage, FiClock } from 'react-icons/fi';
import api from '../api';

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.tif,.tiff,.bmp,.heic,.docx,.csv,.txt';
const fmtMoney = (v) => (v == null || v === '' || isNaN(v)) ? '—' : Number(v).toFixed(2);
const token = () => localStorage.getItem('token') || sessionStorage.getItem('token') || '';
const fileUrl = (id) => `${(api.defaults && api.defaults.baseURL) || ''}/invoice-intake/${id}/file?token=${encodeURIComponent(token())}`;

function useDebounce(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

// ── big menu button ───────────────────────────────────────────────────────
function MenuTile({ icon: Icon, secondary: Secondary, title, text, onClick }) {
  return (
    <Box as="button" onClick={onClick} textAlign="left" bg="white" border="2px solid" borderColor="gray.200" borderRadius="xl" p={6} w="100%" maxW="420px"
      _hover={{ borderColor: 'teal.400', boxShadow: 'md' }} transition="all .15s">
      <HStack spacing={5} align="center">
        <Box position="relative" w="64px" h="64px" flexShrink={0}>
          <Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center" color="teal.500"><Icon size={44} /></Box>
          <Box position="absolute" right="-6px" bottom="-6px" bg="white" borderRadius="full" p="3px" color="orange.500"><Secondary size={24} /></Box>
        </Box>
        <Box>
          <Text fontWeight="700" fontSize="md">{title}</Text>
          <Text fontSize="sm" color="gray.600" mt={1}>{text}</Text>
        </Box>
      </HStack>
    </Box>
  );
}

// ── target search ─────────────────────────────────────────────────────────
function TargetSearch({ onPick, recent = [], compact = false, autoFocus = true }) {
  const [q, setQ] = useState('');
  const dq = useDebounce(q, 450);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (dq.trim().length < 2) { setRows([]); return; }
    let live = true; setBusy(true);
    api.get(`/invoice-intake/search?q=${encodeURIComponent(dq.trim())}`).then(r => { if (live) setRows(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (live) setRows([]); }).finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [dq]);
  return (
    <Box>
      <HStack>
        <Box color="gray.500"><FiSearch /></Box>
        <Input autoFocus={autoFocus} size={compact ? 'sm' : 'md'} placeholder="Type a company, project or task name…" value={q} onChange={e => setQ(e.target.value)} />
        {busy && <Spinner size="sm" />}
      </HStack>
      {recent.length > 0 && !q && (
        <Box mt={3}>
          <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}><FiClock style={{ display: 'inline' }} /> Recently used</Text>
          <VStack align="stretch" spacing={1}>
            {recent.map((r, i) => (
              <Box key={i} as="button" textAlign="left" p={2} borderRadius="md" bg="teal.50" _hover={{ bg: 'teal.100' }} onClick={() => onPick(r)}>
                <Text fontSize="sm" fontWeight="600">{r.project_name}{r.task_title ? ` › ${r.task_title}` : ''}</Text>
                {r.company_names && <Text fontSize="xs" color="gray.600">{r.company_names}</Text>}
              </Box>
            ))}
          </VStack>
        </Box>
      )}
      {rows.length > 0 && (
        <VStack align="stretch" spacing={1} mt={3} maxH="320px" overflowY="auto">
          {rows.map(r => (
            <Box key={r.task_id} as="button" textAlign="left" p={2} borderRadius="md" border="1px solid" borderColor="gray.200" _hover={{ bg: 'gray.50', borderColor: 'teal.300' }} onClick={() => onPick(r)}>
              <Text fontSize="sm" fontWeight="600">{r.task_title}</Text>
              <Text fontSize="xs" color="gray.600">{r.project_name}{r.company_names ? ` · ${r.company_names}` : ''}</Text>
            </Box>
          ))}
        </VStack>
      )}
      {!busy && dq.trim().length >= 2 && rows.length === 0 && <Text fontSize="xs" color="gray.500" mt={2}>No matching tasks.</Text>}
    </Box>
  );
}

// ── drop zone ─────────────────────────────────────────────────────────────
function DropZone({ onFiles, disabled }) {
  const [over, setOver] = useState(false);
  const inputRef = useRef();
  return (
    <Box border="2px dashed" borderColor={over ? 'teal.400' : 'gray.300'} bg={over ? 'teal.50' : 'gray.50'} borderRadius="xl" p={8} textAlign="center"
      opacity={disabled ? 0.5 : 1}
      onDragOver={e => { e.preventDefault(); if (!disabled) setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); if (!disabled && e.dataTransfer.files.length) onFiles(Array.from(e.dataTransfer.files)); }}>
      <Box color="teal.500" mb={2}><FiUpload size={36} style={{ display: 'inline' }} /></Box>
      <Text fontWeight="600">Drag invoices here</Text>
      <Text fontSize="sm" color="gray.600" mb={3}>PDF, images (scans), Word, CSV or text — one or many at a time</Text>
      <Button size="sm" onClick={() => inputRef.current && inputRef.current.click()} isDisabled={disabled}>Choose files…</Button>
      <input ref={inputRef} type="file" multiple accept={ACCEPT} style={{ display: 'none' }} onChange={e => { if (e.target.files.length) onFiles(Array.from(e.target.files)); e.target.value = ''; }} />
    </Box>
  );
}

function statusBadge(item) {
  if (item.uploading) return <Badge colorScheme="blue">uploading…</Badge>;
  if (item.error) return <Badge colorScheme="red">failed</Badge>;
  switch (item.status) {
    case 'approved': return <Badge colorScheme="green"><FiCheckCircle style={{ display: 'inline' }} /> approved</Badge>;
    case 'extracted': return <Badge colorScheme="teal">auto-read · approve</Badge>;
    case 'reviewed': return <Badge colorScheme="purple">edited</Badge>;
    case 'manual': return <Badge colorScheme="orange"><FiImage style={{ display: 'inline' }} /> scan · enter manually</Badge>;
    default: return <Badge colorScheme="yellow"><FiAlertCircle style={{ display: 'inline' }} /> needs review</Badge>;
  }
}

function Thumb({ item }) {
  const isImg = /^image\//.test(item.mime_type || '');
  if (item.uploading || !item.id) return <Flex w="72px" h="92px" bg="gray.100" borderRadius="md" align="center" justify="center"><Spinner size="sm" /></Flex>;
  if (isImg) return <Image src={fileUrl(item.id)} alt="" w="72px" h="92px" objectFit="cover" borderRadius="md" border="1px solid" borderColor="gray.200" />;
  if (/pdf/.test(item.mime_type || '')) return (
    <Box w="72px" h="92px" borderRadius="md" border="1px solid" borderColor="gray.200" overflow="hidden" bg="white" position="relative">
      <object data={`${fileUrl(item.id)}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} type="application/pdf" width="220" height="280" style={{ transform: 'scale(0.33)', transformOrigin: '0 0', pointerEvents: 'none' }} aria-label="preview" />
    </Box>
  );
  return <Flex w="72px" h="92px" bg="gray.100" borderRadius="md" align="center" justify="center" color="gray.500"><FiFileText size={28} /></Flex>;
}

// ── review modal ──────────────────────────────────────────────────────────
function ReviewModal({ item, onClose, onSaved, onApproved, targetPicker }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [supplierQ, setSupplierQ] = useState('');
  const dSupplierQ = useDebounce(supplierQ, 400);
  const [supplierRows, setSupplierRows] = useState([]);
  const [pickTarget, setPickTarget] = useState(false);
  const [target, setTarget] = useState({ project_id: item.project_id, project_name: item.project_name, task_id: item.task_id, task_title: item.task_title });

  useEffect(() => {
    api.get(`/invoice-intake/${item.id}`).then(r => {
      const d = r.data; setData(d);
      const ex = d.extraction || {};
      const base = { ...ex, ...(d.fields || {}) };
      if (!base.supplier_company_id && d.supplier_match && d.supplier_match.company) { base.supplier_company_id = d.supplier_match.company.id; base.supplier_company_name = d.supplier_match.company.name; base.supplier_match_reason = d.supplier_match.reason; }
      base.line_items = (base.line_items || []).map(li => ({ ...li }));
      setF(base);
    }).catch(e => toast({ title: 'Could not load', description: e?.response?.data?.error, status: 'error' }));
  }, [item.id]); // eslint-disable-line

  useEffect(() => {
    if (!dSupplierQ.trim()) { setSupplierRows([]); return; }
    api.get(`/invoice-intake/suppliers?q=${encodeURIComponent(dSupplierQ.trim())}`).then(r => setSupplierRows(r.data || [])).catch(() => setSupplierRows([]));
  }, [dSupplierQ]);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const setItem = (i, k, v) => setF(p => { const items = p.line_items.map((it, j) => j === i ? { ...it, [k]: v } : it); return { ...p, line_items: items }; });
  const recalcFromItems = () => setF(p => {
    const net = p.line_items.reduce((s, it) => s + (parseFloat(it.amount) || (parseFloat(it.quantity) || 1) * (parseFloat(it.unit_price) || 0)), 0);
    const vat = p.line_items.reduce((s, it) => s + ((parseFloat(it.amount) || (parseFloat(it.quantity) || 1) * (parseFloat(it.unit_price) || 0)) * ((it.vat_rate != null ? parseFloat(it.vat_rate) : (p.vat_rate || 0)) / 100)), 0);
    return { ...p, net: +net.toFixed(2), vat: +vat.toFixed(2), gross: +(net + vat).toFixed(2) };
  });
  const sumsOk = f && f.net != null && f.vat != null && f.gross != null && Math.abs((+f.net) + (+f.vat) - (+f.gross)) <= 0.02;

  const save = async (approve) => {
    if (!f) return;
    if (approve && !target.project_id) { setPickTarget(true); toast({ title: 'Choose where this invoice goes first', status: 'warning', duration: 2500 }); return; }
    setSaving(true);
    try {
      const payload = { ...f }; delete payload.supplier_match_reason;
      if (approve) {
        const r = await api.post(`/invoice-intake/${item.id}/approve`, { fields: payload, project_id: target.project_id, task_id: target.task_id });
        toast({ title: 'Invoice recorded', description: `${payload.supplier_name || 'Supplier'} · ${fmtMoney(payload.gross)} ${payload.currency || 'GBP'}`, status: 'success', duration: 2500 });
        onApproved(r.data);
      } else {
        const r = await api.put(`/invoice-intake/${item.id}`, { fields: payload, project_id: target.project_id, task_id: target.task_id });
        onSaved(r.data); toast({ title: 'Saved', status: 'success', duration: 1200 });
      }
    } catch (e) { toast({ title: approve ? 'Approve failed' : 'Save failed', description: e?.response?.data?.error || e.message, status: 'error', duration: 4000 }); }
    finally { setSaving(false); }
  };
  const reextract = async () => {
    setSaving(true);
    try { const r = await api.post(`/invoice-intake/${item.id}/reextract`); onSaved(r.data); setF({ ...(r.data.extraction || {}), line_items: (r.data.extraction?.line_items || []).map(x => ({ ...x })) }); toast({ title: 'Re-read from the document', status: 'info', duration: 1500 }); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.error, status: 'error' }); }
    finally { setSaving(false); }
  };

  const approved = item.status === 'approved';
  const isImg = /^image\//.test(item.mime_type || '');
  const ex = data?.extraction || {};
  return (
    <Modal isOpen onClose={onClose} size="full" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader py={3} borderBottom="1px solid" borderColor="gray.200">
          <HStack justify="space-between" pr={10} minW={0}>
            <HStack minW={0} flex="1 1 auto"><FiFileText /><Text fontSize="md" isTruncated minW={0}>{item.original_name}</Text>{statusBadge(item)}</HStack>
            <HStack spacing={2} flexShrink={0}>
              {!approved && <Button size="sm" variant="ghost" leftIcon={<FiRefreshCw />} onClick={reextract} isLoading={saving}>Auto-populate again</Button>}
              {!approved && <Button size="sm" variant="outline" onClick={() => save(false)} isLoading={saving}>Save</Button>}
              {!approved && <Button size="sm" colorScheme="green" leftIcon={<FiCheckCircle />} onClick={() => save(true)} isLoading={saving}>Approve &amp; record invoice</Button>}
            </HStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={0}>
          <Flex h="calc(100vh - 64px - 57px)" direction={{ base: 'column', lg: 'row' }} overflow="hidden" minW={0}>
            {/* left: the document */}
            <Box flex="1 1 55%" minW={0} bg="gray.100" minH="300px">
              {isImg
                ? <Flex h="100%" align="center" justify="center" p={4} overflow="auto"><Image src={fileUrl(item.id)} alt="" maxW="100%" maxH="100%" objectFit="contain" /></Flex>
                : <object data={fileUrl(item.id)} type={item.mime_type || 'application/pdf'} width="100%" height="100%" aria-label="document">
                    <Flex h="100%" align="center" justify="center"><Button as="a" href={fileUrl(item.id)} target="_blank" rel="noreferrer">Open document</Button></Flex>
                  </object>}
            </Box>
            {/* right: interpreted information */}
            {/* FormControls default to width:100%, so several in an HStack overflow
                the pane (and pushed the header buttons off-screen) — make every
                control in this pane share the row instead. */}
            <Box flex="1 1 45%" minW={0} overflowY="auto" overflowX="hidden" p={5} borderLeft="1px solid" borderColor="gray.200"
              sx={{ '.chakra-form-control': { flex: '1 1 0', minWidth: 0 } }}>
              {!f ? <Flex justify="center" py={10}><Spinner /></Flex> : (
                <VStack align="stretch" spacing={4}>
                  {item.status === 'manual' && (
                    <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3} fontSize="sm">
                      <b>Scanned image</b> — no readable text was found, so nothing was auto-populated. Enter the details from the image on the left. (Automatic reading of scans is a planned addition.)
                    </Box>
                  )}
                  {ex.warnings && ex.warnings.length > 0 && item.status !== 'manual' && (
                    <Box bg="yellow.50" border="1px solid" borderColor="yellow.200" borderRadius="md" p={3} fontSize="xs">
                      {ex.warnings.map((w, i) => <Text key={i}>• {w}</Text>)}
                    </Box>
                  )}
                  {/* target */}
                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase">Goes to</Text>
                      {!approved && <Button size="xs" variant="link" onClick={() => setPickTarget(v => !v)}>{target.project_id ? 'Change' : 'Choose project & task'}</Button>}
                    </HStack>
                    {target.project_id
                      ? <Text fontSize="sm"><FiFolder style={{ display: 'inline' }} /> <b>{target.project_name}</b>{target.task_title ? ` › ${target.task_title}` : ''}</Text>
                      : <Text fontSize="sm" color="red.600">Not yet assigned to a project.</Text>}
                    {pickTarget && !approved && <Box mt={2} p={3} bg="gray.50" borderRadius="md">{targetPicker((t) => { setTarget({ project_id: t.project_id, project_name: t.project_name, task_id: t.task_id, task_title: t.task_title }); setPickTarget(false); })}</Box>}
                  </Box>
                  <Divider />
                  {/* supplier */}
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>Supplier</Text>
                    <HStack align="flex-start">
                      <FormControl>
                        <FormLabel fontSize="xs" mb={0.5}>Name on the invoice</FormLabel>
                        <Input size="sm" value={f.supplier_name || ''} onChange={e => set('supplier_name', e.target.value)} isDisabled={approved} />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="xs" mb={0.5}>Supplier VAT number</FormLabel>
                        <Input size="sm" value={f.supplier_vat_number || ''} onChange={e => set('supplier_vat_number', e.target.value)} isDisabled={approved} placeholder="e.g. GB123456789" />
                      </FormControl>
                    </HStack>
                    <Box mt={2}>
                      {f.supplier_company_id
                        ? <HStack><Tag colorScheme="teal" size="sm">Linked to company: {f.supplier_company_name || 'selected'}{f.supplier_match_reason ? ` (matched by ${f.supplier_match_reason})` : ''}</Tag>
                            {!approved && <Button size="xs" variant="ghost" onClick={() => { set('supplier_company_id', null); set('supplier_company_name', null); }}>unlink</Button>}</HStack>
                        : (<Box>
                            <HStack><Input size="sm" placeholder="Link to an existing company (search by name or VAT number)…" value={supplierQ} onChange={e => setSupplierQ(e.target.value)} isDisabled={approved} />
                              <Tooltip label={f.create_supplier === false ? 'A new supplier company will NOT be created' : 'A new supplier company will be created from the name above if no link is chosen'}>
                                <HStack><Text fontSize="xs">Create if new</Text><Switch size="sm" isChecked={f.create_supplier !== false} onChange={e => set('create_supplier', e.target.checked)} isDisabled={approved} /></HStack>
                              </Tooltip></HStack>
                            {supplierRows.length > 0 && <VStack align="stretch" spacing={0} mt={1} border="1px solid" borderColor="gray.200" borderRadius="md" maxH="160px" overflowY="auto">
                              {supplierRows.map(c => <Box key={c.id} as="button" textAlign="left" px={2} py={1} fontSize="sm" _hover={{ bg: 'gray.50' }}
                                onClick={() => { set('supplier_company_id', c.id); set('supplier_company_name', c.name); if (!f.supplier_vat_number && c.vat_number) set('supplier_vat_number', c.vat_number); setSupplierQ(''); setSupplierRows([]); }}>
                                {c.name}{c.vat_number ? <Text as="span" fontSize="xs" color="gray.500"> · {c.vat_number}</Text> : null}</Box>)}
                            </VStack>}
                          </Box>)}
                    </Box>
                  </Box>
                  <Divider />
                  {/* header fields */}
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>Invoice</Text>
                    <HStack align="flex-start">
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Invoice number</FormLabel>
                        <Input size="sm" value={f.invoice_number || ''} onChange={e => set('invoice_number', e.target.value)} isDisabled={approved} placeholder={f.order_reference ? `order ref ${f.order_reference}` : ''} /></FormControl>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Order / reference</FormLabel>
                        <Input size="sm" value={f.order_reference || ''} onChange={e => set('order_reference', e.target.value)} isDisabled={approved} /></FormControl>
                    </HStack>
                    <HStack align="flex-start" mt={2}>
                      <FormControl isRequired><FormLabel fontSize="xs" mb={0.5}>Invoice date</FormLabel>
                        <Input size="sm" type="date" value={f.issue_date || ''} onChange={e => set('issue_date', e.target.value)} isDisabled={approved} /></FormControl>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Due date</FormLabel>
                        <Input size="sm" type="date" value={f.due_date || ''} onChange={e => set('due_date', e.target.value)} isDisabled={approved} /></FormControl>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Currency</FormLabel>
                        <Select size="sm" value={f.currency || 'GBP'} onChange={e => set('currency', e.target.value)} isDisabled={approved}><option>GBP</option><option>EUR</option><option>USD</option></Select></FormControl>
                    </HStack>
                    <HStack align="flex-start" mt={2}>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Net</FormLabel>
                        <Input size="sm" type="number" step="0.01" value={f.net ?? ''} onChange={e => set('net', e.target.value === '' ? null : +e.target.value)} isDisabled={approved} /></FormControl>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>VAT</FormLabel>
                        <Input size="sm" type="number" step="0.01" value={f.vat ?? ''} onChange={e => set('vat', e.target.value === '' ? null : +e.target.value)} isDisabled={approved} /></FormControl>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>VAT rate %</FormLabel>
                        <Input size="sm" type="number" step="0.5" value={f.vat_rate ?? ''} onChange={e => set('vat_rate', e.target.value === '' ? null : +e.target.value)} isDisabled={approved} /></FormControl>
                      <FormControl isRequired><FormLabel fontSize="xs" mb={0.5}>Total (gross)</FormLabel>
                        <Input size="sm" type="number" step="0.01" fontWeight="700" value={f.gross ?? ''} onChange={e => set('gross', e.target.value === '' ? null : +e.target.value)} isDisabled={approved} /></FormControl>
                    </HStack>
                    <HStack mt={2} spacing={4} fontSize="xs">
                      {f.net != null && f.vat != null && f.gross != null && (sumsOk ? <Text color="green.600"><FiCheckCircle style={{ display: 'inline' }} /> net + VAT = total</Text> : <Text color="red.600"><FiAlertCircle style={{ display: 'inline' }} /> net + VAT ≠ total — check the figures</Text>)}
                      <HStack><Switch size="sm" isChecked={!!f.paid} onChange={e => set('paid', e.target.checked)} isDisabled={approved} /><Text>Already paid</Text></HStack>
                      <HStack><Switch size="sm" isChecked={f.is_vat_invoice !== false} onChange={e => set('is_vat_invoice', e.target.checked)} isDisabled={approved} /><Text>VAT invoice</Text></HStack>
                      {f.reverse_charge && <Badge colorScheme="purple">reverse charge</Badge>}
                    </HStack>
                    <FormControl mt={2}><FormLabel fontSize="xs" mb={0.5}>Status when recorded</FormLabel>
                      <Select size="sm" maxW="280px" value={f.status || (f.paid ? 'recv_paid_in_full' : 'recv_pay_within_terms')} onChange={e => set('status', e.target.value)} isDisabled={approved}>
                        <option value="recv_pay_within_terms">To pay within terms</option>
                        <option value="recv_paid_in_full">Paid in full</option>
                        <option value="recv_assign_po">Assign PO</option>
                        <option value="recv_receipt">Receipt (card / already settled)</option>
                        <option value="recv_disputed">Disputed</option>
                      </Select></FormControl>
                  </Box>
                  <Divider />
                  {/* line items */}
                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase">Line items (optional — totals above are what count)</Text>
                      {!approved && <HStack><Button size="xs" variant="ghost" onClick={recalcFromItems}>Totals from lines</Button><Button size="xs" leftIcon={<FiPlus />} onClick={() => setF(p => ({ ...p, line_items: [...p.line_items, { description: '', quantity: 1, unit_price: 0, vat_rate: p.vat_rate ?? 20, amount: 0 }] }))}>Add line</Button></HStack>}
                    </HStack>
                    <Table size="sm" variant="simple">
                      <Thead><Tr><Th>Description</Th><Th isNumeric w="70px">Qty</Th><Th isNumeric w="100px">Unit</Th><Th isNumeric w="80px">VAT %</Th><Th isNumeric w="100px">Amount</Th><Th w="32px"></Th></Tr></Thead>
                      <Tbody>
                        {f.line_items.map((it, i) => (
                          <Tr key={i}>
                            <Td p={1}><Input size="xs" value={it.description || ''} onChange={e => setItem(i, 'description', e.target.value)} isDisabled={approved} /></Td>
                            <Td p={1}><Input size="xs" type="number" value={it.quantity ?? 1} onChange={e => setItem(i, 'quantity', +e.target.value)} isDisabled={approved} /></Td>
                            <Td p={1}><Input size="xs" type="number" step="0.001" value={it.unit_price ?? ''} onChange={e => setItem(i, 'unit_price', +e.target.value)} isDisabled={approved} /></Td>
                            <Td p={1}><Input size="xs" type="number" step="0.5" value={it.vat_rate ?? ''} onChange={e => setItem(i, 'vat_rate', e.target.value === '' ? null : +e.target.value)} isDisabled={approved} /></Td>
                            <Td p={1}><Input size="xs" type="number" step="0.01" value={it.amount ?? ''} onChange={e => setItem(i, 'amount', +e.target.value)} isDisabled={approved} /></Td>
                            <Td p={1}>{!approved && <IconButton size="xs" variant="ghost" icon={<FiX />} aria-label="remove" onClick={() => setF(p => ({ ...p, line_items: p.line_items.filter((_, j) => j !== i) }))} />}</Td>
                          </Tr>
                        ))}
                        {f.line_items.length === 0 && <Tr><Td colSpan={6}><Text fontSize="xs" color="gray.500">No lines — a single summary line will be recorded from the totals.</Text></Td></Tr>}
                      </Tbody>
                    </Table>
                    {f.line_items.some(li => li.amount_includes_vat) && <Text fontSize="xs" color="orange.600" mt={1}>Line amounts on this document include VAT.</Text>}
                  </Box>
                  {data && <Text fontSize="10px" color="gray.400">Read by: {data.method || '—'} · confidence {Math.round((data.confidence || 0) * 100)}%</Text>}
                </VStack>
              )}
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter borderTop="1px solid" borderColor="gray.200" py={2} gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose}>{approved ? 'Close' : 'Close without approving'}</Button>
          <Box flex="1" />
          {!approved && <Button size="sm" variant="outline" onClick={() => save(false)} isLoading={saving}>Save for later</Button>}
          {!approved && <Button size="sm" colorScheme="green" leftIcon={<FiCheckCircle />} onClick={() => save(true)} isLoading={saving}>Approve &amp; record invoice</Button>}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ── dashboard ─────────────────────────────────────────────────────────────
export default function InvoiceIntakeDashboard({ onBack }) {
  const toast = useToast();
  const [mode, setMode] = useState(null);            // 'single' | 'multi'
  const [target, setTarget] = useState(null);        // single mode target
  const [recent, setRecent] = useState([]);
  const [items, setItems] = useState([]);
  const [review, setReview] = useState(null);
  const [assigning, setAssigning] = useState(null);  // multi mode: item id picking a target

  const loadRecent = useCallback(() => api.get('/invoice-intake/recent-targets').then(r => setRecent(r.data || [])).catch(() => {}), []);
  const loadItems = useCallback(() => api.get('/invoice-intake').then(r => setItems(r.data || [])).catch(() => {}), []);
  useEffect(() => { loadRecent(); loadItems(); }, [loadRecent, loadItems]);

  const uploadFiles = async (files, tgt) => {
    const temp = files.map(fl => ({ tempId: Math.random().toString(36).slice(2), original_name: fl.name, uploading: true, mime_type: fl.type }));
    setItems(prev => [...temp, ...prev]);
    for (let i = 0; i < files.length; i++) {           // one at a time so the list fills progressively
      const fd = new FormData();
      fd.append('files', files[i]);
      if (tgt?.project_id) fd.append('project_id', tgt.project_id);
      if (tgt?.task_id) fd.append('task_id', tgt.task_id);
      try {
        const r = await api.post('/invoice-intake/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const created = r.data[0];
        setItems(prev => prev.map(it => it.tempId === temp[i].tempId ? (created.error ? { ...it, uploading: false, error: created.error } : created) : it));
      } catch (e) {
        setItems(prev => prev.map(it => it.tempId === temp[i].tempId ? { ...it, uploading: false, error: e?.response?.data?.error || e.message } : it));
      }
    }
    loadRecent();
  };

  const replaceItem = (d) => setItems(prev => prev.map(it => it.id === d.id ? { ...it, ...d } : it));
  const discard = async (it) => {
    if (!window.confirm(`Discard ${it.original_name}?`)) return;
    try { await api.delete(`/invoice-intake/${it.id}`); setItems(prev => prev.filter(x => x.id !== it.id)); } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.error, status: 'error' }); }
  };
  const assign = async (it, t) => {
    try { const r = await api.put(`/invoice-intake/${it.id}`, { project_id: t.project_id, task_id: t.task_id }); replaceItem(r.data); setAssigning(null); loadRecent(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.error, status: 'error' }); }
  };
  const targetPicker = (onPick) => <TargetSearch onPick={onPick} recent={recent} compact />;

  const list = (
    <VStack align="stretch" spacing={2} mt={4}>
      {items.map(it => (
        <Flex key={it.id || it.tempId} p={2} bg="white" border="1px solid" borderColor={it.error ? 'red.200' : 'gray.200'} borderRadius="md" align="center" gap={3}
          cursor={it.id && !it.uploading ? 'pointer' : 'default'} onClick={() => it.id && !it.uploading && setReview(it)} _hover={it.id ? { borderColor: 'teal.300' } : {}}>
          <Thumb item={it} />
          <Box flex={1} minW={0}>
            <HStack><Text fontWeight="600" fontSize="sm" isTruncated>{it.original_name}</Text>{statusBadge(it)}</HStack>
            {it.error && <Text fontSize="xs" color="red.600">{it.error}</Text>}
            {it.extraction && (
              <Text fontSize="xs" color="gray.700" mt={0.5}>
                {it.extraction.supplier_name || <i>supplier?</i>} · {it.extraction.invoice_number || it.extraction.order_reference || <i>no number</i>} · {it.extraction.issue_date || <i>no date</i>} ·
                {' '}net {fmtMoney(it.extraction.net)} · VAT {fmtMoney(it.extraction.vat)} · <b>total {fmtMoney(it.extraction.gross)}</b>{it.extraction.supplier_vat_number ? ` · VAT no. ${it.extraction.supplier_vat_number}` : ''}
              </Text>
            )}
            <Text fontSize="xs" color={it.project_id ? 'gray.500' : 'orange.600'} mt={0.5}>
              {it.project_id ? <><FiFolder style={{ display: 'inline' }} /> {it.project_name}{it.task_title ? ` › ${it.task_title}` : ''}</> : (it.id && !it.uploading ? 'No project yet — choose one' : '')}
              {it.invoice_number && it.status === 'approved' ? ` · recorded as ${it.invoice_number}` : ''}
            </Text>
            {mode === 'multi' && it.id && it.status !== 'approved' && (
              <Box mt={1} onClick={e => e.stopPropagation()}>
                {assigning === it.id
                  ? <Box p={2} bg="gray.50" borderRadius="md"><TargetSearch onPick={(t) => assign(it, t)} recent={recent} compact /><Button size="xs" variant="ghost" mt={1} onClick={() => setAssigning(null)}>Cancel</Button></Box>
                  : <Button size="xs" variant="outline" leftIcon={<FiFolder />} onClick={() => setAssigning(it.id)}>{it.project_id ? 'Change project / task' : 'Choose project / task'}</Button>}
              </Box>
            )}
          </Box>
          <HStack onClick={e => e.stopPropagation()}>
            {it.id && !it.uploading && <Tooltip label={it.status === 'approved' ? 'View' : 'Review & approve'}><IconButton size="sm" variant="ghost" icon={<FiEdit3 />} aria-label="review" onClick={() => setReview(it)} /></Tooltip>}
            {it.id && it.status !== 'approved' && <Tooltip label="Discard"><IconButton size="sm" variant="ghost" colorScheme="red" icon={<FiTrash2 />} aria-label="discard" onClick={() => discard(it)} /></Tooltip>}
          </HStack>
        </Flex>
      ))}
      {items.length === 0 && <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>Nothing uploaded yet.</Text>}
    </VStack>
  );

  return (
    <Box p={6} maxW="1100px" mx="auto">
      <HStack mb={5}>
        <IconButton icon={<FiArrowLeft />} aria-label="back" variant="ghost" onClick={() => (mode ? (setMode(null), setTarget(null)) : onBack && onBack())} />
        <Text fontSize="xl" fontWeight="700">Upload Invoices</Text>
        {mode === 'single' && target && <Badge colorScheme="teal" fontSize="sm"><FiFolder style={{ display: 'inline' }} /> {target.project_name} › {target.task_title}</Badge>}
      </HStack>

      {!mode && (
        <VStack spacing={4} align="stretch" maxW="420px">
          <MenuTile icon={FiUpload} secondary={FiFolder} title="Upload invoices to a specific project and task"
            text="Choose one project and task, then drop one or many invoices — they all go there." onClick={() => setMode('single')} />
          <MenuTile icon={FiUpload} secondary={FiLayers} title="Upload one or more invoices to different projects and tasks"
            text="Drop the invoices first, then choose where each one goes — recently used targets are one click." onClick={() => setMode('multi')} />
        </VStack>
      )}

      {mode === 'single' && !target && (
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={5} maxW="640px">
          <Text fontWeight="600" mb={3}>Which project and task?</Text>
          <TargetSearch onPick={setTarget} recent={recent} />
        </Box>
      )}
      {mode === 'single' && target && (<>
        <DropZone onFiles={(files) => uploadFiles(files, target)} />
        <HStack mt={2}><Button size="xs" variant="link" onClick={() => setTarget(null)}>Change project / task</Button></HStack>
        {list}
      </>)}
      {mode === 'multi' && (<>
        <DropZone onFiles={(files) => uploadFiles(files, null)} />
        <Text fontSize="xs" color="gray.600" mt={2}>Each invoice below needs a project and task before it can be approved — recently used ones are offered first.</Text>
        {list}
      </>)}

      {review && <ReviewModal item={review} onClose={() => setReview(null)} targetPicker={targetPicker}
        onSaved={(d) => { replaceItem(d); setReview(r => ({ ...r, ...d })); }}
        onApproved={(d) => { replaceItem(d); setReview(null); loadRecent(); }} />}
    </Box>
  );
}
